import React, { useState, useEffect } from 'react';
import { BookOpen, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { articleService } from '../services/api';
import ArticleDetailModal from './ArticleDetailModal';

const Articles = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const categories = [
    { id: 'all', name: 'All Articles' },
    { id: 'disabilityresources', name: 'Disabilty Resources' },
    { id: 'housingassets', name: 'Housing and Assets' },
    { id: 'financesmoney', name: 'Finances and Money' },
    { id: 'governmentsocialprograms', name: 'Governement/Social Programs' },
    { id: 'buildingsupport', name: 'Building Support' }
  ];

  // Fetch articles when category changes
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await articleService.getArticles(selectedCategory);
        setArticles(response.articles || []);
      } catch (err) {
        setError('Failed to load articles');
        console.error('Error fetching articles:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticles();
  }, [selectedCategory]);

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    // Prevent scrolling of the background when modal is open
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setSelectedArticle(null);
    // Restore scrolling when modal is closed
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      <div className="max-w-6xl mx-auto p-6">
        {/* Hero Section */}
        <div className="bg-blue-50 rounded-lg p-8 mb-8 flex items-center space-x-6">
          <BookOpen className="w-16 h-16 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold mb-2">Education and Training</h1>
            <p className="text-gray-600">
              Expert articles, training and guidance to help you make informed decisions. 
            </p>
          </div>
        </div>

        {/* Category Selection */}
        <div className="mb-8 flex justify-between items-center">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full max-w-xs p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Articles Display */}
        <div className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 text-red-600 rounded-lg">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-2" />
              <p className="text-gray-500">Loading articles...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No articles found in this category.</p>
            </div>
          ) : (
            articles.map(article => (
              <Card 
                key={article.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleArticleClick(article)}
              >
                <CardHeader>
                  <CardTitle>{article.title}</CardTitle>
                  <div className="text-sm text-gray-500 flex items-center justify-between">
                    <span>{new Date(article.dateCreated).toLocaleDateString()}</span>
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs">
                      {categories.find(c => c.id === article.category)?.name || article.category}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    {article.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block bg-gray-100 text-gray-800 mr-2 mb-2 px-2 py-1 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="text-gray-600 line-clamp-3">{article.content}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Article Detail Modal */}
      {selectedArticle && (
        <ArticleDetailModal
          article={selectedArticle}
          categories={categories}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default Articles;